/**
 * FileTree
 *
 * The left column of the editor: every file in the mission working directory,
 * drawn as a tree the learner can walk with the keyboard alone.
 *
 * The API hands back a *flat* list — one entry per file and per directory,
 * depth first, paths relative to the mission cwd with forward slashes. This
 * component's real work is turning that list back into a shape, sorting it the
 * way a file browser sorts (directories first, then alphabetical), and keeping
 * exactly one row tabbable so the tree is a single tab stop rather than fifty.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api } from '../../api';
import { Machine, Parts } from '../../ui';
import { useLocale, useStrings } from '../../state/useLocale';
import type { FileEntry } from '../../types';

export interface FileTreeProps {
  sessionId: string;
  /** Path of the file open in the editor, or null. Drawn as selected. */
  selected: string | null;
  /** Path of the open file while it has unsaved changes, else null. */
  dirtyPath: string | null;
  /** Mission working directory. Labels the root of the tree. */
  rootLabel: string;
  /** Change this to refetch the listing, e.g. after a save or a reset. */
  refreshKey: number;
  onOpen(path: string): void;
  /** Every listing that arrives, for a host that needs to know what is in the workspace. */
  onEntries?(entries: readonly FileEntry[]): void;
}

interface Node {
  name: string;
  /** Full path relative to the mission cwd. */
  path: string;
  dir: boolean;
  size: number;
  children: Node[];
}

interface Row {
  node: Node;
  depth: number;
  /** Path of the containing directory, or null at the top level. */
  parent: string | null;
  setSize: number;
  posInSet: number;
}

/* ── Shaping the listing ──────────────────────────────────────────────────── */

/**
 * Rebuild the nested tree from the flat listing.
 *
 * Directories arrive as their own entries, but a listing truncated at the
 * server's entry cap can still mention `a/b/c.txt` without `a/b`, so parents
 * are created on demand rather than assumed.
 */
function buildTree(entries: readonly FileEntry[]): Node[] {
  const root: Node = { name: '', path: '', dir: true, size: 0, children: [] };
  const dirs = new Map<string, Node>([['', root]]);

  const ensureDir = (path: string): Node => {
    const found = dirs.get(path);
    if (found) return found;
    const cut = path.lastIndexOf('/');
    const parent = ensureDir(cut === -1 ? '' : path.slice(0, cut));
    const node: Node = { name: path.slice(cut + 1), path, dir: true, size: 0, children: [] };
    parent.children.push(node);
    dirs.set(path, node);
    return node;
  };

  const seen = new Set<string>();
  for (const entry of entries) {
    const path = entry.path;
    if (!path || seen.has(path)) continue;
    seen.add(path);
    if (entry.dir) {
      ensureDir(path);
      continue;
    }
    const cut = path.lastIndexOf('/');
    ensureDir(cut === -1 ? '' : path.slice(0, cut)).children.push({
      name: path.slice(cut + 1),
      path,
      dir: false,
      size: entry.size,
      children: [],
    });
  }

  const sort = (node: Node): void => {
    node.children.sort((a, b) =>
      a.dir === b.dir ? a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }) : a.dir ? -1 : 1,
    );
    for (const child of node.children) sort(child);
  };
  sort(root);
  return root.children;
}

/** Walk the visible part of the tree into the row list the DOM mirrors. */
function flatten(nodes: readonly Node[], expanded: ReadonlySet<string>): Row[] {
  const out: Row[] = [];
  const walk = (list: readonly Node[], depth: number, parent: string | null): void => {
    list.forEach((node, i) => {
      out.push({ node, depth, parent, setSize: list.length, posInSet: i + 1 });
      if (node.dir && expanded.has(node.path)) walk(node.children, depth + 1, node.path);
    });
  };
  walk(nodes, 0, null);
  return out;
}

/** "storefront/src" → "src"; "" → the catalogue's stand-in name. */
function rootName(cwd: string, fallback: string): string {
  const clean = cwd.replace(/\\/g, '/').replace(/\/+$/, '');
  const cut = clean.lastIndexOf('/');
  return (cut === -1 ? clean : clean.slice(cut + 1)) || fallback;
}

/* ── Component ────────────────────────────────────────────────────────────── */

export function FileTree({ sessionId, selected, dirtyPath, rootLabel, refreshKey, onOpen, onEntries }: FileTreeProps) {
  const s = useStrings().play.files;
  const dir = useLocale().dir;
  const rtl = dir === 'rtl';
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [active, setActive] = useState<string | null>(null);

  // Read through a ref so that a host passing an inline callback does not
  // refetch the listing on every render.
  const entriesRef = useRef(onEntries);
  useEffect(() => {
    entriesRef.current = onEntries;
  });

  /* --- Listing ----------------------------------------------------------- */

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.listFiles(sessionId).then(
      (list) => {
        if (cancelled) return;
        setEntries(list);
        setError(null);
        setLoading(false);
        entriesRef.current?.(list);
      },
      (err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : s.listFailed);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshKey, s]);

  const tree = useMemo(() => buildTree(entries), [entries]);

  // The root's immediate children start open; everything deeper starts shut,
  // so a big repository still fits on one screen. Done once per session, so a
  // refresh never re-opens what the learner deliberately collapsed.
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (seeded.current === sessionId || tree.length === 0) return;
    seeded.current = sessionId;
    setExpanded(new Set(tree.filter((n) => n.dir).map((n) => n.path)));
  }, [sessionId, tree]);

  // Opening a file from anywhere else (a future "jump to file") should reveal
  // it rather than leave the selection hidden inside a closed directory.
  useEffect(() => {
    if (!selected) return;
    const parts = selected.split('/');
    if (parts.length < 2) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      let acc = '';
      let changed = false;
      for (const part of parts.slice(0, -1)) {
        acc = acc ? `${acc}/${part}` : part;
        if (!next.has(acc)) {
          next.add(acc);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selected]);

  const rows = useMemo(() => flatten(tree, expanded), [tree, expanded]);

  /* --- Roving focus ------------------------------------------------------- */

  const rowEls = useRef(new Map<string, HTMLDivElement>());
  const wanted = useRef<string | null>(null);

  // Runs after every render, which is what makes ArrowRight-into-a-child work:
  // the child row does not exist until the expansion has been painted.
  useEffect(() => {
    const path = wanted.current;
    if (path === null) return;
    wanted.current = null;
    rowEls.current.get(path)?.focus();
  });

  const focusRow = useCallback((path: string) => {
    wanted.current = path;
    setActive(path);
  }, []);

  const setExpandedFor = useCallback((path: string, open: boolean) => {
    setExpanded((prev) => {
      if (prev.has(path) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(path);
      else next.delete(path);
      return next;
    });
  }, []);

  // The tabbable row: the selection if it is visible, otherwise the first row.
  const firstPath = rows[0]?.node.path ?? null;
  const activePath =
    active !== null && rows.some((r) => r.node.path === active) ? active : (selected !== null && rows.some((r) => r.node.path === selected) ? selected : firstPath);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const index = rows.findIndex((r) => r.node.path === activePath);
      if (index === -1) return;
      const row = rows[index];
      if (!row) return;
      const { node } = row;
      const isOpen = node.dir && expanded.has(node.path);

      const move = (to: number): void => {
        const target = rows[Math.max(0, Math.min(rows.length - 1, to))];
        if (target) focusRow(target.node.path);
      };

      // WAI-ARIA's tree pattern is written in "inward" and "outward", not in
      // left and right: the key that opens a directory is the one pointing
      // the way the tree indents, and a mirrored tree indents the other way.
      // So ArrowLeft expands and ArrowRight collapses when the page is RTL.
      const inward = rtl ? 'ArrowLeft' : 'ArrowRight';
      const outward = rtl ? 'ArrowRight' : 'ArrowLeft';

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          move(index + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          move(index - 1);
          break;
        case 'Home':
          event.preventDefault();
          move(0);
          break;
        case 'End':
          event.preventDefault();
          move(rows.length - 1);
          break;
        case inward:
          event.preventDefault();
          if (node.dir && !isOpen) {
            setExpandedFor(node.path, true);
            focusRow(node.path);
          } else if (isOpen) {
            move(index + 1);
          }
          break;
        case outward:
          event.preventDefault();
          if (isOpen) setExpandedFor(node.path, false);
          else if (row.parent) focusRow(row.parent);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (node.dir) setExpandedFor(node.path, !isOpen);
          else onOpen(node.path);
          break;
        default:
          break;
      }
    },
    [rows, activePath, expanded, focusRow, setExpandedFor, onOpen, rtl],
  );

  /* --- Render ------------------------------------------------------------- */

  const body = (): ReactNode => {
    if (error) {
      // The message is the API's own, in English, so it is isolated rather
      // than run together with the Arabic around it.
      return (
        <p className="tree-empty note">
          <Parts of={s.unavailable(<Machine key="err">{error}</Machine>)} />
        </p>
      );
    }
    if (loading && entries.length === 0) return <p className="tree-empty note">{s.reading}</p>;
    if (rows.length === 0) return <p className="tree-empty note">{s.empty}</p>;

    return (
      <div className="tree" role="tree" aria-label={s.treeLabel} onKeyDown={onKeyDown}>
        {rows.map(({ node, depth, setSize, posInSet }) => {
          const isOpen = node.dir && expanded.has(node.path);
          const isSelected = !node.dir && node.path === selected;
          return (
            <div
              key={node.path}
              ref={(el) => {
                if (el) rowEls.current.set(node.path, el);
                else rowEls.current.delete(node.path);
              }}
              className={[
                'tree-row',
                node.dir ? 'is-dir' : 'is-file',
                isSelected ? 'is-selected' : '',
                node.path === activePath ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              role="treeitem"
              aria-level={depth + 1}
              aria-setsize={setSize}
              aria-posinset={posInSet}
              aria-expanded={node.dir ? isOpen : undefined}
              aria-selected={node.dir ? undefined : isSelected}
              tabIndex={node.path === activePath ? 0 : -1}
              title={node.path}
              // Logical, not physical: the tree indents away from the edge
              // the reader starts at, which is the right-hand one in Arabic.
              style={{ paddingInlineStart: `calc(var(--sp-2) + ${depth} * 13px)` }}
              onClick={() => {
                setActive(node.path);
                if (node.dir) setExpandedFor(node.path, !isOpen);
                else onOpen(node.path);
              }}
            >
              {/* A shut directory points the way it would open, which is
                  inward — leftward once the tree is mirrored. Open points
                  down in both. */}
              <span className="tree-twisty" aria-hidden="true">
                {node.dir ? (isOpen ? '▾' : rtl ? '◂' : '▸') : ''}
              </span>
              {/* A file name is the machine's: `README.md` must not be
                  reordered around its dot in an Arabic paragraph. */}
              <span className="tree-name" dir="ltr">
                {node.name}
              </span>
              {node.path === dirtyPath && <span className="tree-dirty" aria-label={s.unsavedChanges} />}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    // The tree itself follows the page: it is a shape, drawn with indentation
    // and a twisty, and a shape that does not mirror leaves an island of
    // left-to-right layout in the middle of a right-to-left screen. The names
    // inside it do not follow the page — each one is marked `dir="ltr"`
    // below, which is what docs/i18n.md §7 is protecting.
    //
    // Stated on the element rather than inherited because locale.css turns
    // the whole of `.editor` around it left-to-right for CodeMirror's sake,
    // and this pane is not CodeMirror.
    <div className="filetree" dir={dir}>
      <div className="filetree-head">
        <span className="label">{s.head}</span>
        {/* The mission's own directory name. A path, so it keeps its
            direction whatever the page is set to. */}
        <span className="filetree-root" title={rootLabel || '.'} dir="ltr">
          {rootName(rootLabel, s.rootFallback)}
        </span>
      </div>
      <div className="filetree-body">{body()}</div>
    </div>
  );
}

export default FileTree;
